import z from "zod";

export const PairedDeviceSchema = z.object({
  code: z.string(),
  deviceId: z.string(),
  expiry: z.string(),
  hostname: z.string().optional(),
  createdBy: z.string(),
  createdAt: z.string(),
  modifiedBy: z.string(),
  modifiedAt: z.string(),
  isArchived: z.boolean(),
});
export type PairedDevice = z.infer<typeof PairedDeviceSchema>;

export const PairedDevicesListSchema = z.array(PairedDeviceSchema);
export type PairedDevicesList = z.infer<typeof PairedDevicesListSchema>;

export const RevokedDeviceSchema = z.object({
  deviceId: z.string(),
  revokedAt: z.string(),
});
export type RevokedDevice = z.infer<typeof RevokedDeviceSchema>;

export const DeviceIdParamsSchema = z.object({
  deviceId: z.string(),
});

export const PairingIntentIdParamsSchema = z.object({
  intentId: z.string(),
});

export const PairingIntentSchema = z.object({
  intentId: z.string(),
  hostname: z.string(),
  // The app's own runtime.GOOS, so the approval page can name the machine it is
  // connecting. Absent from an app build that predates the field.
  platform: z.string().optional(),
  confirmationCode: z.string().regex(/^\d{6}$/),
  status: z.enum(["pending", "approved", "completed", "expired"]),
  expiresAt: z.string(),
});
export type PairingIntent = z.infer<typeof PairingIntentSchema>;

// Turn fields are PascalCase because the Go `chief.Turn` struct has no JSON
// tags and serializes with Go's default field-name encoding.
export const ChiefSessionTurnSchema = z.object({
  Speaker: z.number(),
  Source: z.string(),
  Text: z.string(),
  Start: z.number(),
  End: z.number(),
  // Speaker identification told this voice apart; on the microphone that
  // means it is not taken for the host.
  Identified: z.boolean().optional(),
  // Who identification recognised the voice as. An enrolled member's voiceprint
  // is named by their account email, so this can be an address.
  Name: z.string().optional(),
});
export type ChiefSessionTurn = z.infer<typeof ChiefSessionTurnSchema>;

export const ChiefSessionActionSchema = z.object({
  text: z.string(),
  owner: z.string(),
  speaker: z.string(),
});
export type ChiefSessionAction = z.infer<typeof ChiefSessionActionSchema>;

export const ChiefSessionDecisionSchema = z.object({
  text: z.string(),
  speaker: z.string(),
});
export type ChiefSessionDecision = z.infer<typeof ChiefSessionDecisionSchema>;

export const ChiefSessionContextItemSchema = z.object({
  text: z.string(),
});
export type ChiefSessionContextItem = z.infer<typeof ChiefSessionContextItemSchema>;

// One LLM relay tick. tickedAtSec is the audio-time of the latest observed
// turn when the tick was assembled — every item in the tick shares it. Older
// sessions persisted before this field existed won't have it. The action/
// decision/context arrays are nullish because the newer relay records ticks in
// the liveSummary shape instead, leaving these keys absent on recent sessions.
export const ChiefSessionRelaySchema = z.object({
  actions: z.array(ChiefSessionActionSchema).nullish(),
  decisions: z.array(ChiefSessionDecisionSchema).nullish(),
  context: z.array(ChiefSessionContextItemSchema).nullish(),
  tickedAtSec: z.number().optional(),
});
export type ChiefSessionRelay = z.infer<typeof ChiefSessionRelaySchema>;

// One reconciled bullet of the live summary (the newer artifact that replaces the
// per-tick relay shape). `active` false means the item sits in its topic's
// collapsed "set aside" sub-section; `state` "dismissed" hides it entirely.
// `parentId`, when set, nests the item as a sub-point under that main point.
const ChiefSummaryItemSchema = z.object({
  id: z.string(),
  // "note" is a bullet a participant wrote themselves in the desktop app; the
  // rest come from the model.
  kind: z.enum(["context", "decision", "todo", "note"]),
  topic: z.string().optional(),
  text: z.string(),
  owner: z.string().optional(),
  speaker: z.string().optional(),
  importance: z.number().optional(),
  state: z.string().optional(),
  active: z.boolean(),
  pinned: z.boolean().optional(),
  parentId: z.string().optional(),
  firstSeenSec: z.number().optional(),
  // The transcript line a note was written against. Absent on model items.
  quote: z.string().optional(),
  tagIds: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? []),
  // What a Smart Tag applied, and what somebody took off by hand. Kept apart
  // from tagIds so the two never merge on the way back through a relay pass;
  // the shared view merges them for display.
  autoTagIds: z.array(z.string()).optional(),
  removedAutoTagIds: z.array(z.string()).optional(),
});

// One tag in a project's live-session vocabulary. Shared by everyone in the
// project; created and applied only in the Chief desktop app, so this schema
// only ever backs a read.
//
// `deleted` marks a tag no longer offered. The list still carries it because a
// session renders whatever its content carries, and the definition has to stay
// resolvable after someone removes the tag from the vocabulary.
//
// A Smart Tag applies itself: `instruction` is the sentence it matches on, and
// `defaultKind` is set on the three every project starts with, whose matching is
// the summary's own classification rather than a sentence.
export const ChiefTagSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string(),
  instruction: z.string().optional(),
  smart: z.boolean().optional(),
  defaultKind: z.string().optional(),
  system: z.boolean().optional(),
  pinned: z.boolean().optional(),
  deleted: z.boolean().optional(),
});

export const ListChiefTagsResponseSchema = z.array(ChiefTagSchema);

// The Tags page: the tagged points of the project's recent sessions, each
// point's tags as stored, for the page to resolve against the vocabulary the
// way Notes does. `partial` is set when a session couldn't be read.
export const ChiefTaggedItemsResponseSchema = z.object({
  sessions: z.array(z.object({ id: z.string(), name: z.string(), at: z.string().optional() })),
  items: z.array(
    z.object({
      sessionId: z.string(),
      itemId: z.string(),
      kind: z.string(),
      text: z.string(),
      atSec: z.number(),
      tagIds: z.array(z.string()).optional(),
      autoTagIds: z.array(z.string()).optional(),
      removedAutoTagIds: z.array(z.string()).optional(),
    })
  ),
  partial: z.boolean().optional(),
});

// The live summary: a one-sentence headline plus topic-grouped items. Sessions
// recorded before this artifact existed have no items (a nil slice → null).
// Items carry tag ids only — the definitions come from the project's tag list,
// so a rename shows up everywhere at once.
export const ChiefLiveSummarySchema = z.object({
  headline: z.string(),
  items: z.array(ChiefSummaryItemSchema).nullable(),
});

// The live endpoint wraps each type-specific payload in this transport
// envelope. Payload validation belongs to the consumer because turn, context,
// state, brief, and terminal events intentionally have different shapes.
export const ChiefLiveEventSchema = z.object({
  type: z.string(),
  data: z.unknown().optional(),
});
export type ChiefLiveEvent = z.infer<typeof ChiefLiveEventSchema>;

// A chat thread spawned during a session. `kind` is "primer" | "develop" |
// "relay" | "highlight"; the web console surfaces "develop" chats (fanned out
// from a suggested action) and "highlight" chats (opened from a transcript
// passage in the native app). `title` is the source action text on develops and
// the highlighted passage on highlights.
const ChiefSessionChatSchema = z.object({
  kind: z.string(),
  chatId: z.string(),
  title: z.string().optional(),
  // The user's tags on the chat, so a tag gathers a question alongside the
  // notes and bullets about the same thing. Ids only — the definitions come
  // from the project's vocabulary.
  tagIds: z.array(z.string()).optional(),
});

// An Activity card the answer pass put in front of the room, as Chief showed
// it. `answerId` is what a card link names. Additions are lines appended after
// the first; one whose `check` is "corrected" was rewritten in place by a later
// check, so its text is the correction.
const ChiefSessionCardSchema = z.object({
  answerId: z.string(),
  title: z.string(),
  body: z.string(),
  asked: z.string().optional(),
  additions: z.array(z.object({ id: z.string(), text: z.string(), check: z.string().optional() })).optional(),
  sources: z.array(z.object({ title: z.string(), url: z.string().optional() })).optional(),
  basis: z.string().optional(),
  check: z.string().optional(),
  withdrawn: z.boolean().optional(),
  shownAt: z.string(),
});

// The rest of what Chief put in the session's Activity, as the host stored it.
// Only the fields the server always writes are required; a record written
// before a field existed still parses.
const ChiefSessionFindingSchema = z.object({
  claim: z.string(),
  citations: z
    .array(z.object({ chunkId: z.string(), title: z.string().optional(), url: z.string().optional() }))
    .nullish(),
  spokenAtSec: z.number().optional(),
  heardThroughSec: z.number().optional(),
});

const ChiefSessionOfferSchema = z.object({
  offerId: z.string(),
  // The Notes point the offer is about; absent on one Chief raised on its own.
  itemId: z.string().optional(),
  what: z.string(),
  why: z.string().nullish(),
  status: z.string().nullish(),
  spokenAtSec: z.number().optional(),
  heardThroughSec: z.number().optional(),
});

const ChiefSessionAgentRunSchema = z.object({
  requestId: z.string().optional(),
  ref: z.string().optional(),
  label: z.string().optional(),
  ask: z.string().optional(),
  state: z.string().nullish(),
});

// Session lifecycle states, mirroring the `chief.SessionState*` constants the
// backend writes. A session is "live" precisely while it is Started.
export const ChiefSessionStates = {
  Scheduled: "session.scheduled",
  Started: "session.started",
  Ended: "session.ended",
} as const;

// Lifecycle state of a session. `state` is one of the `session.*` constants
// (scheduled/started/ended); `startedAt` is present only once the session has
// reached that phase, so it is optional. `scheduledAt` is absent on a session
// that began recording without being scheduled.
const ChiefSessionStateSchema = z.object({
  state: z.string(),
  scheduledAt: z.string().optional(),
  startedAt: z.string().optional(),
});

// A personal session is one user's private screen-help container in a project:
// it never records, so it has no transcript, only chats.
const ChiefSessionKindSchema = z.enum(["meeting", "personal"]);
export type ChiefSessionKind = z.infer<typeof ChiefSessionKindSchema>;

const sessionMetaFields = {
  id: z.string(),
  name: z.string(),
  // An API that predates kinds sends none, and every session it has is a
  // meeting. A kind added later also reads as a meeting until this learns it.
  kind: ChiefSessionKindSchema.catch("meeting"),
  description: z.string(),
  state: ChiefSessionStateSchema,
  tenantId: z.string(),
  organizationId: z.string(),
  workspaceId: z.string(),
  projectId: z.string(),
  createdAt: z.string(),
  createdBy: z.string(),
  modifiedAt: z.string(),
  modifiedBy: z.string(),
};

export const ChiefSessionMetaSchema = z.object(sessionMetaFields);
export type ChiefSessionMeta = z.infer<typeof ChiefSessionMetaSchema>;

export const ChiefSessionSchema = z.object({
  ...sessionMetaFields,
  summary: z.string().optional(),
  actionItems: z.array(z.string()).optional(),
  // The host's user-editable session context; read-only everywhere but the
  // hosting device. Absent on sessions that never set one.
  brief: z.string().optional(),
  // The host device's account of what the session's agents did, written once
  // when the meeting ends. Absent when no agent ran.
  agentDigest: z.string().optional(),
  notionPageUrl: z.string().optional(),
  turns: z.array(ChiefSessionTurnSchema).nullable(),
  context: z.array(ChiefSessionRelaySchema).nullable(),
  liveSummary: ChiefLiveSummarySchema.nullish(),
  chats: z.array(ChiefSessionChatSchema).nullish(),
  // Activity is extra to the page: a list that fails to parse is dropped rather
  // than costing the reader the whole session.
  cards: z.array(ChiefSessionCardSchema).nullish().catch(null),
  findings: z.array(ChiefSessionFindingSchema).nullish().catch(null),
  offers: z.array(ChiefSessionOfferSchema).nullish().catch(null),
  agentRuns: z.array(ChiefSessionAgentRunSchema).nullish().catch(null),
});
export type ChiefSession = z.infer<typeof ChiefSessionSchema>;

export const ListChiefSessionsResponseSchema = z.object({
  data: z.array(ChiefSessionMetaSchema),
  first_id: z.string().optional(),
  last_id: z.string().optional(),
  has_more: z.boolean(),
});

export const ListChiefSessionsParamsSchema = z.object({
  limit: z.number().optional(),
  afterId: z.string().optional(),
  beforeId: z.string().optional(),
  kind: ChiefSessionKindSchema.optional(),
});

export const ChiefSessionIdParamsSchema = z.object({
  sessionId: z.string(),
});

// Where a project-less session link leads. "elsewhere" covers both a session in
// another workspace and one that does not exist; projectId is set only when the
// caller can open the session.
export const ChiefSessionLocationSchema = z.object({
  reach: z.enum(["project", "session", "none", "elsewhere"]),
  projectId: z.string().optional(),
});

export const UpdateChiefSessionNameBodySchema = z.object({
  name: z.string(),
});

export const ChiefSessionNameResponseSchema = z.object({
  sessionId: z.string(),
  name: z.string(),
  modifiedAt: z.string(),
});
export type ChiefSessionNameResponse = z.infer<typeof ChiefSessionNameResponseSchema>;

export const MoveChiefSessionsBodySchema = z.object({
  sessionIds: z.array(z.string()),
  projectId: z.string(),
});

// The counts report the cascade, not the request: a session's chats and linked
// assets follow it, so the totals exceed the session count and let the caller
// tell the user what else travelled.
export const MoveChiefSessionsResponseSchema = z.object({
  sessionIds: z
    .array(z.string())
    .nullable()
    .transform((v) => v ?? []),
  chats: z.number(),
  assets: z.number(),
});
export type MoveChiefSessionsResponse = z.infer<typeof MoveChiefSessionsResponseSchema>;

export const StartForNotionPageBodySchema = z.object({
  pageId: z.string(),
  databaseId: z.string().optional(),
  // Pins resolution when the database is deployed to several projects.
  projectId: z.string().optional(),
});

// state distinguishes a launchable session (scheduled/started) from an ended
// one the caller should just display.
export const StartForNotionPageResponseSchema = z.object({
  sessionId: z.string(),
  projectId: z.string(),
  state: z.string(),
});

export const AddChiefSessionChatBodySchema = z.object({
  chatId: z.string(),
  title: z.string(),
});

// One item pinned into a transcript, at the anchor it was added at
// (afterTurnIndex is the count of turns present at the time). What else it
// carries depends on mediaKind: an image or markup visual has an asset id, a
// signed GET URL the <img> loads, and the stored type; pasted text has none of
// those and carries the text instead. id — the transcript link row — is the only
// way to address a paste, and it is optional only because a control plane that
// predates it sends images without one; identity falls back to the asset id
// there rather than failing the whole list's parse.
//
// mediaKind stays a plain string rather than an enum: a kind added server-side
// must not fail the whole list's parse on a client that predates it.
export const ChiefSessionMediaItemSchema = z.object({
  id: z.string().optional(),
  assetId: z.string().optional(),
  afterTurnIndex: z.number(),
  atSeconds: z.number(),
  url: z.string().optional(),
  contentType: z.string().optional(),
  mediaKind: z.string().optional(),
  pastedText: z.string().optional(),
  filename: z.string().optional(),
});
export type ChiefSessionMediaItem = z.infer<typeof ChiefSessionMediaItemSchema>;

// Go serializes a nil slice as JSON null; normalize to [].
export const ListChiefSessionMediaResponseSchema = z.object({
  items: z
    .array(ChiefSessionMediaItemSchema)
    .nullable()
    .transform((v) => v ?? []),
});

export const PublishChiefSessionMediaBodySchema = z.object({
  assetIds: z.array(z.string()),
});

export const ChiefPublishedMediaSchema = z.object({
  assetId: z.string(),
  publicUrl: z.string(),
});
export type ChiefPublishedMedia = z.infer<typeof ChiefPublishedMediaSchema>;

export const PublishChiefSessionMediaResponseSchema = z.object({
  items: z.array(ChiefPublishedMediaSchema),
});

// What a share link shows: the whole session, or one section of it. Each scope
// holds a link of its own.
export const ChiefShareScopeSchema = z.enum(["session", "notes", "transcript", "workspace", "decisions", "todos"]);
export type ChiefShareScope = z.infer<typeof ChiefShareScopeSchema>;

export const ChiefSessionShareParamsSchema = z.object({
  sessionId: z.string(),
  scope: ChiefShareScopeSchema,
});

// A collaborate link lets its holder join the session; only the whole session has one.
export const ChiefSessionShareBodySchema = z.object({
  tier: z.enum(["watch", "collaborate"]),
  scope: ChiefShareScopeSchema,
});

// Share-link state for a session, as returned by the share create/status
// endpoints. `token` is only present while the session is shared.
export const ChiefSessionShareStatusSchema = z.object({
  isShared: z.boolean(),
  token: z.string().optional(),
  tier: z.string().optional(),
  createdAt: z.string().optional(),
});

export const ChiefShareEmailsBodySchema = z.object({
  emails: z.array(z.email()).min(1).max(10),
});

export const ChiefShareEmailsResponseSchema = z.object({
  sent: z.number(),
  failed: z
    .array(
      z.object({
        email: z.string(),
        reason: z.string(),
      })
    )
    .optional(),
});

export const ChiefShareTokenParamsSchema = z.object({
  token: z.string(),
});

// One inline transcript image shown to a share-link guest. Pasted text never
// appears here — the guest snapshot withholds it the same way the live stream
// does.
export const PublicChiefSessionMediaItemSchema = z.object({
  assetId: z.string(),
  afterTurnIndex: z.number(),
  atSeconds: z.number(),
  url: z.string(),
  contentType: z.string().optional(),
});

// One document or canvas the Watch link selected. Content is the stored
// projection as of the last quiesce, so it lags the room a member reads.
const PublicChiefSessionArtifactSchema = z.object({
  id: z.string(),
  title: z.string(),
  kind: z.string(),
  content: z
    .record(z.string(), z.unknown())
    .nullish()
    .transform((v) => v ?? undefined),
  plainText: z.string(),
  updatedAt: z.string(),
});

// The guest view of a shared session: lifecycle without calendar/meeting
// linkage, plus the live summary, transcript, inline images, and recap.
export const PublicChiefSessionSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  brief: z.string().optional(),
  // Present so a logged-in visitor with project access can be redirected to
  // the full session page; grants nothing by itself.
  projectId: z.string().optional(),
  // Last write to the session row. A running capture rewrites it on every
  // flush, so it is what separates a live session from one whose host crashed
  // and left the row Started; nothing server-side ever ends such a row. It
  // defaults rather than being required so a bundle deployed ahead of the API
  // still parses — a miss would fail the whole guest read, not just the badge.
  modifiedAt: z
    .string()
    .optional()
    .default(() => new Date().toISOString()),
  state: z.object({
    state: z.string(),
    startedAt: z.string().optional(),
  }),
  liveSummary: ChiefLiveSummarySchema.nullish(),
  turns: z.array(ChiefSessionTurnSchema).nullable(),
  media: z
    .array(PublicChiefSessionMediaItemSchema)
    .nullish()
    .transform((v) => v ?? []),
  summary: z.string().optional(),
  // The link's own tier; a collaborate link is joined in Chief, not on the web.
  tier: z.string().optional(),
  // What the link shows. A section link's response already leaves the other
  // sections out; the page only lays out what it was sent.
  scope: z.enum(["session", "transcript", "notes", "decisions", "todos", "workspace"]).optional(),
  actionItems: z.array(z.string()).optional(),
  // The documents and canvases the link selected; absent from a response served
  // by an API deployed before the selection existed.
  artifacts: z
    .array(PublicChiefSessionArtifactSchema)
    .nullish()
    .transform((v) => v ?? []),
  // The project's session tag vocabulary, so a guest renders the tags on summary
  // points. Absent from an API deployed before guests saw tags, and null when the
  // server could not read it; the page falls back to Chief's defaults then.
  tags: z.array(ChiefTagSchema).nullish(),
});
export type PublicChiefSession = z.infer<typeof PublicChiefSessionSchema>;

// The guest view of a shared session document. Content is the stored TipTap
// projection, which is only as fresh as the last quiesce — a guest read never
// loads the document's collaboration room.
export const PublicChiefArtifactSchema = z.object({
  title: z.string(),
  kind: z.string(),
  content: z.record(z.string(), z.unknown()),
  plainText: z.string(),
  updatedAt: z.string(),
});

// Images in a shared document resolve one signed URL at a time; an asset with
// no live placement in this document comes back with an error instead of a URL.
export const PublicChiefArtifactImagesBodySchema = z.object({
  assetIds: z.array(z.string()),
});

export const PublicChiefArtifactImagesResponseSchema = z.object({
  urls: z
    .array(
      z.object({
        assetId: z.string(),
        url: z.string().optional(),
        expiresAt: z.string().optional(),
        error: z.string().optional(),
      })
    )
    .nullable()
    .transform((v) => v ?? []),
});

// Send-to-agent instructions written from the session's own record. The body
// carries the record rather than a session reference: a still-recording session
// has more on the client than the server has been told about yet.
export const SuggestChiefSessionPromptsBodySchema = z.object({
  brief: z.string(),
  summary: z.string(),
  toDos: z.array(z.string()),
  decisions: z.array(z.string()),
  transcript: z.string(),
});

export const ChiefSuggestedPromptSchema = z.object({
  label: z.string(),
  prompt: z.string(),
});
export type ChiefSuggestedPrompt = z.infer<typeof ChiefSuggestedPromptSchema>;

// A session with nothing worth suggesting from comes back with no prompts, and
// Go serializes that empty slice as null.
export const SuggestChiefSessionPromptsResponseSchema = z.object({
  prompts: z
    .array(ChiefSuggestedPromptSchema)
    .nullable()
    .transform((v) => v ?? []),
});
